import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ImageLibraryModel, ImageLibraryRequestOptions } from '@models/types'

const watchEntityKeys = ['imageLibraries']

type ImageLibraryFilters = {
  hidden?: 'true' | 'false' | undefined,
  string?: string,
  owner_type?: string,
  owner_id?: number,
}

type UseImageLibrariesOptions = {
  filters?: ImageLibraryFilters,
  performHttpRequests?: boolean,
  requestOptions?: ImageLibraryRequestOptions,
}

function useImageLibraries(options: UseImageLibrariesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    hidden: filterHidden,
    string: filterString,
    owner_type: filterOwnerType,
    owner_id: filterOwnerId,
  } = filters

  const {
    updatedEntities: { imageLibraries: imageLibrariesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { imageLibraries } = useSelector(reduxState => reduxState.entities)

  const filteredImageLibraries = useMemo(() => {
    const filtered = Object.values(imageLibraries).filter((imageLibrary: ImageLibraryModel) => {
      const {
        hidden, id, owner_id, owner_type, title,
      } = imageLibrary

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true

      return matchHidden && matchString && matchOwnerId && matchOwnerType
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [imageLibrariesUpdatedAt, JSON.stringify(filters)])

  const filteredImageLibrariesCount = filteredImageLibraries.length
  const hasFilteredImageLibraries = !!filteredImageLibrariesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredImageLibrariesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // @ts-expect-error TS7022
  const { loading: loadingImageLibraries } = useReduxAction(
    'imageLibraries',
    'loadImageLibraries',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS7023
      shouldPerformFn: () => performHttpRequests && !loadingImageLibraries,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredImageLibraries,
    filteredImageLibrariesCount,
    hasFilteredImageLibraries,
    loading: loadingImageLibraries,
  }
}

export default useImageLibraries
